import { useState } from "react";
import { useForm } from "react-hook-form";
import { useNotification } from "../../context/NotificationContext";
import { Link } from "react-router-dom";
import client from "../../api/client";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
 Card,
 CardContent,
 CardDescription,
 CardHeader,
 CardTitle,
} from "../../components/ui/card";
import { Alert, AlertDescription } from "../../components/ui/alert";
import { Loader2, Mail, CheckCircle2, XCircle, ArrowLeft } from "lucide-react";
import VerificationModal from "../../components/VerificationModal";

function ForgotPassword() {
 const {
 register,
 handleSubmit,
 formState: { errors },
 } = useForm();
 const { add } = useNotification();
 const [loading, setLoading] = useState(false);
 const [message, setMessage] = useState("");
 const [error, setError] = useState("");
 const [showModal, setShowModal] = useState(false);

 const onSubmit = async (data) => {
 setLoading(true);
 setError("");
 setMessage("");

 try {
 await client.post("/auth/forgot-password", data);
 setMessage("If the email exists, a reset link has been sent!");
 add("Reset link sent! Check your email.", "success");
 setShowModal(true);
 } catch (err) {
 console.error(err);
 setError(err.response?.data?.message || "Something went wrong");
 add(err.response?.data?.message || "Something went wrong", "error");
 } finally {
 setLoading(false);
 }
 };

 const handleCloseModal = () => {
 setShowModal(false);
 };

 return (
 <div className="min-h-screen flex items-center justify-center p-4">
 <div className="w-full max-w-md">
 <Card className="shadow-lg">
 <CardHeader className="space-y-1">
 <div className="flex items-center justify-between mb-2">
 <Link 
 to="/login" 
 className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground transition-colors"
 >
 <ArrowLeft className="w-4 h-4 mr-1" />
 Back to login
 </Link>
 </div>
 <div className="flex items-center gap-2">
 <div className="p-2 rounded-lg bg-primary/10">
 <Mail className="w-5 h-5 text-primary" />
 </div>
 <CardTitle className="text-2xl font-bold">
 Forgot Password
 </CardTitle>
 </div>
 <CardDescription>
 Enter your email address and we'll send you a link to reset your password.
 </CardDescription>
 </CardHeader>
 <CardContent>
 {message && (
 <Alert className="mb-4 border-green-500/50 bg-green-500/100/10">
 <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400" />
 <AlertDescription className="text-green-600 dark:text-green-400">
 {message}
 </AlertDescription>
 </Alert>
 )}
 {error && (
 <Alert variant="destructive" className="mb-4">
 <XCircle className="h-4 w-4" />
 <AlertDescription>{error}</AlertDescription>
 </Alert>
 )}

 <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
 <div className="space-y-2">
 <Label htmlFor="email">Email Address</Label>
 <Input
 id="email"
 type="email"
 {...register("email", {
 required: "Email is required",
 pattern: {
 value: /^\S+@\S+$/i,
 message: "Invalid email address",
 },
 })}
 placeholder="you@example.com"
 />
 {errors.email && (
 <p className="text-sm text-destructive">{errors.email.message}</p>
 )}
 </div>

 <Button
 type="submit"
 className="w-full"
 disabled={loading}
 >
 {loading ? (
 <>
 <Loader2 className="mr-2 h-4 w-4 animate-spin" />
 Sending...
 </>
 ) : (
 <>
 <Mail className="mr-2 h-4 w-4" />
 Send Reset Link
 </>
 )}
 </Button>

 <div className="text-center text-sm">
 <span className="text-muted-foreground">Remember your password? </span>
 <Link to="/login" className="text-primary hover:underline">
 Sign in
 </Link>
 </div>
 </form>
 </CardContent>
 </Card>
 </div>

 {/* Verification Modal */}
 <VerificationModal isOpen={showModal} onClose={handleCloseModal} />
 </div>
 );
}

export default ForgotPassword;
